/**
 * Email Verification Page
 * Allows users to verify their email with a code sent during registration
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Mail, CheckCircle2 } from 'lucide-react';
import { authService } from '@/services/auth.service';

// Validation schema
const verifySchema = z.object({
  email: z.string().email('Invalid email address'),
  code: z.string().min(6, 'Verification code must be at least 6 characters'),
});

type VerifyFormData = z.infer<typeof verifySchema>;

export function VerifyEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Pre-fill email and code from URL params
  const [formData, setFormData] = useState<VerifyFormData>({
    email: searchParams.get('email') || '',
    code: searchParams.get('code') || '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof VerifyFormData, string>>>({});
  const [serverError, setServerError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Auto-submit if both email and code are pre-filled from URL
  useEffect(() => {
    const emailFromUrl = searchParams.get('email');
    const codeFromUrl = searchParams.get('code');

    if (emailFromUrl && codeFromUrl) {
      // Validate and auto-submit
      const result = verifySchema.safeParse({ email: emailFromUrl, code: codeFromUrl });
      if (result.success) {
        handleSubmit(new Event('submit') as any, true);
      }
    }
  }, []);

  const handleChange =
    (field: keyof VerifyFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
      // Clear field error on change
      if (errors[field]) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }
      // Clear server error on change
      if (serverError) {
        setServerError('');
      }
    };

  const handleSubmit = async (e: React.FormEvent, _isAutoSubmit = false) => {
    e.preventDefault();

    // Clear previous errors
    setErrors({});
    setServerError('');
    setShowSuccess(false);

    // Validate form data
    const result = verifySchema.safeParse(formData);

    if (!result.success) {
      const fieldErrors: Partial<Record<keyof VerifyFormData, string>> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as keyof VerifyFormData] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    // Submit to backend
    setIsLoading(true);
    try {
      await authService.confirmEmail({
        email: formData.email,
        code: formData.code,
      });

      // Show success message
      setShowSuccess(true);

      // Redirect to login after a delay
      setTimeout(() => {
        navigate('/login', {
          state: {
            message: 'Email verified! Please log in with your credentials.',
          },
        });
      }, 2000);
    } catch (error) {
      setServerError(
        error instanceof Error ? error.message : 'Verification failed. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background p-4">
      <main className="w-full max-w-md">
        <Card className="w-full">
          <CardHeader className="space-y-3">
            <div className="flex justify-center">
              <div className="rounded-full bg-primary/10 p-3">
                <Mail className="h-6 w-6 text-primary" />
              </div>
            </div>
            <CardTitle className="text-center">Verify Your Email</CardTitle>
            <CardDescription className="text-center">
              Enter your email and the verification code we sent you
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              {showSuccess && (
                <Alert className="border-green-200 bg-green-50 text-green-900">
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertDescription>
                    Email verified successfully! Redirecting to login...
                  </AlertDescription>
                </Alert>
              )}

              {serverError && (
                <Alert variant="destructive">
                  <AlertDescription>{serverError}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange('email')}
                  disabled={isLoading || showSuccess}
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                />
                {errors.email && (
                  <p id="email-error" className="text-sm text-destructive">
                    {errors.email}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="code">Verification Code</Label>
                <Input
                  id="code"
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={formData.code}
                  onChange={handleChange('code')}
                  disabled={isLoading || showSuccess}
                  aria-invalid={!!errors.code}
                  aria-describedby={errors.code ? 'code-error' : undefined}
                  maxLength={6}
                  className="text-center font-mono text-2xl tracking-widest"
                />
                {errors.code && (
                  <p id="code-error" className="text-sm text-destructive">
                    {errors.code}
                  </p>
                )}
                <p className="text-center text-xs text-muted-foreground">
                  Check your email for the 6-digit verification code
                </p>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col space-y-4">
              <Button type="submit" className="w-full" disabled={isLoading || showSuccess}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify Email'
                )}
              </Button>

              <div className="space-y-2 text-center">
                <p className="text-sm text-muted-foreground">
                  Didn't receive the code?{' '}
                  <button
                    type="button"
                    className="text-primary hover:underline"
                    disabled={isLoading || showSuccess}
                    onClick={() => {
                      // TODO: Implement resend code functionality
                      alert('Resend functionality coming soon!');
                    }}
                  >
                    Resend
                  </button>
                </p>
                <p className="text-sm text-muted-foreground">
                  <Link to="/login" className="text-primary hover:underline">
                    Back to login
                  </Link>
                </p>
              </div>
            </CardFooter>
          </form>
        </Card>
      </main>
    </div>
  );
}
